import type {
  PuppyProjectModelDocument,
  PuppyProjectModelOccurrence,
  PuppyProjectModelVersionReference,
} from './types'
import { PUPPY_PROJECT_MODEL_VERSION } from './types'

/**
 * 判断未知值是否为普通对象。
 *
 * @param value 待判断值。
 * @returns 非空且非数组对象返回 true。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * 校验必需非空字符串。
 *
 * @param value 待校验值。
 * @param label 错误上下文。
 * @returns 去除首尾空白后的字符串。
 */
function assertRequiredText(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`PuppyCAD 项目 occurrence 文档缺少 ${label}`)
  }
  return value.trim()
}

/**
 * 解析可选字符串。
 *
 * @param value 待解析值。
 * @param label 错误上下文。
 * @returns 缺省时返回 undefined，否则返回去除空白后的字符串。
 */
function parseOptionalText(value: unknown, label: string): string | undefined {
  if (value === undefined) {
    return undefined
  }
  return assertRequiredText(value, label)
}

/**
 * 解析 occurrence 版本引用。
 *
 * @param value 待解析值。
 * @param occurrenceId occurrence 错误上下文。
 * @returns 结构化版本引用；缺省时返回 undefined。
 */
function parseVersionReference(
  value: unknown,
  occurrenceId: string,
): PuppyProjectModelVersionReference | undefined {
  if (value === undefined) {
    return undefined
  }
  if (!isRecord(value)) {
    throw new Error(`PuppyCAD 项目 occurrence 版本引用必须是对象：${occurrenceId}`)
  }
  return {
    repositoryId: assertRequiredText(value.repositoryId, `repositoryId：${occurrenceId}`),
    ...(value.branchId !== undefined
      ? { branchId: assertRequiredText(value.branchId, `branchId：${occurrenceId}`) }
      : {}),
    commitId: assertRequiredText(value.commitId, `commitId：${occurrenceId}`),
  }
}

/**
 * 解析单个项目 occurrence。
 *
 * @param value 待解析值。
 * @param index occurrence 数组下标。
 * @returns 通过字段校验的 occurrence。
 */
function parseOccurrence(value: unknown, index: number): PuppyProjectModelOccurrence {
  if (!isRecord(value)) {
    throw new Error(`PuppyCAD 项目 occurrence 必须是对象：index=${index}`)
  }
  const occurrenceId = assertRequiredText(value.occurrenceId, `occurrenceId：index=${index}`)
  const parentOccurrenceId = value.parentOccurrenceId === null
    ? null
    : assertRequiredText(value.parentOccurrenceId, `parentOccurrenceId：${occurrenceId}`)
  if (value.nodeType !== 'assembly' && value.nodeType !== 'part') {
    throw new Error(`PuppyCAD 项目 occurrence 节点类型无效：${occurrenceId}/${String(value.nodeType)}`)
  }

  return {
    occurrenceId,
    parentOccurrenceId,
    assemblyNodeId: assertRequiredText(value.assemblyNodeId, `assemblyNodeId：${occurrenceId}`),
    ...(parseOptionalText(value.persistedNodeId, `persistedNodeId：${occurrenceId}`)
      ? { persistedNodeId: assertRequiredText(value.persistedNodeId, `persistedNodeId：${occurrenceId}`) }
      : {}),
    ...(parseOptionalText(value.instanceId, `instanceId：${occurrenceId}`)
      ? { instanceId: assertRequiredText(value.instanceId, `instanceId：${occurrenceId}`) }
      : {}),
    name: assertRequiredText(value.name, `name：${occurrenceId}`),
    nodeType: value.nodeType,
    ...(value.versionReference !== undefined
      ? { versionReference: parseVersionReference(value.versionReference, occurrenceId)! }
      : {}),
  }
}

/**
 * 校验 occurrence 父子图完整且全部可从根到达。
 *
 * @param document 待校验项目文档。
 */
function validateOccurrenceGraph(document: PuppyProjectModelDocument): void {
  const occurrenceById = new Map<string, PuppyProjectModelOccurrence>()
  document.occurrences.forEach((occurrence) => {
    if (occurrenceById.has(occurrence.occurrenceId)) {
      throw new Error(`PuppyCAD 项目 occurrence ID 重复：${occurrence.occurrenceId}`)
    }
    occurrenceById.set(occurrence.occurrenceId, occurrence)
  })
  const rootOccurrence = occurrenceById.get(document.rootOccurrenceId)
  if (!rootOccurrence) {
    throw new Error(`PuppyCAD 项目 occurrence 缺少根节点：${document.rootOccurrenceId}`)
  }
  if (rootOccurrence.parentOccurrenceId !== null) {
    throw new Error(`PuppyCAD 项目 occurrence 根节点不能存在父节点：${document.rootOccurrenceId}`)
  }
  document.occurrences.forEach((occurrence) => {
    if (occurrence.occurrenceId === document.rootOccurrenceId) {
      return
    }
    if (!occurrence.parentOccurrenceId || !occurrenceById.has(occurrence.parentOccurrenceId)) {
      throw new Error(
        `PuppyCAD 项目 occurrence 引用未知父节点：${occurrence.occurrenceId}/${occurrence.parentOccurrenceId ?? ''}`,
      )
    }
  })

  const childIdsByOccurrenceId = new Map<string, string[]>()
  document.occurrences.forEach((occurrence) => {
    if (!occurrence.parentOccurrenceId) {
      return
    }
    const childIds = childIdsByOccurrenceId.get(occurrence.parentOccurrenceId) ?? []
    childIds.push(occurrence.occurrenceId)
    childIdsByOccurrenceId.set(occurrence.parentOccurrenceId, childIds)
  })
  const visited = new Set<string>()
  const visiting = new Set<string>()
  const visit = (occurrenceId: string): void => {
    if (visiting.has(occurrenceId)) {
      throw new Error(`PuppyCAD 项目 occurrence 图存在循环：${occurrenceId}`)
    }
    if (visited.has(occurrenceId)) {
      return
    }
    visiting.add(occurrenceId)
    ;(childIdsByOccurrenceId.get(occurrenceId) ?? []).forEach(visit)
    visiting.delete(occurrenceId)
    visited.add(occurrenceId)
  }
  visit(document.rootOccurrenceId)
  if (visited.size !== document.occurrences.length) {
    const unreachableIds = document.occurrences
      .map((occurrence) => occurrence.occurrenceId)
      .filter((occurrenceId) => !visited.has(occurrenceId))
    throw new Error(`PuppyCAD 项目 occurrence 图存在不可达节点：${unreachableIds.join(',')}`)
  }
}

/**
 * 解析并校验离线包项目 occurrence 文档。
 *
 * @param value 未知 JSON 数据。
 * @returns 通过结构和父子图校验的项目文档。
 * @throws 当输入不是对象、版本不受支持或缺少非空 `occurrences` 数组时。
 */
export function parsePuppyProjectModelDocument(value: unknown): PuppyProjectModelDocument {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD 项目 occurrence 文档必须是对象')
  }
  if (value.version !== PUPPY_PROJECT_MODEL_VERSION) {
    throw new Error(`不支持的 PuppyCAD 项目 occurrence 文档版本：${String(value.version)}`)
  }
  if (!Array.isArray(value.occurrences) || value.occurrences.length === 0) {
    throw new Error('PuppyCAD 项目 occurrence 文档缺少 occurrences')
  }
  const document: PuppyProjectModelDocument = {
    version: PUPPY_PROJECT_MODEL_VERSION,
    rootOccurrenceId: assertRequiredText(value.rootOccurrenceId, 'rootOccurrenceId'),
    occurrences: value.occurrences.map(parseOccurrence),
  }
  validateOccurrenceGraph(document)
  return document
}
